import { couchTopFromBody } from './couchTopFromBody';
import { StructureSdf } from '~/domain/structures/structureSdf';
import { couchTopFromImage } from './couchTopFromImage';
import { Grid } from '~/engine/data/common/Grid';

/**
 * Where a couch-top line came from. Surfaced to the user, because the
 * three are not equally direct and a reader deciding whether to drag the
 * line should know which one they are looking at.
 *
 * `'default'` is not a reading: it is the fixed position a caller seeds
 * when neither reading finds a couch. {@link findCouchTop} never returns
 * it — it reports null and leaves the fallback to the caller that owns
 * the default — but it is named here so that a line placed without a
 * reading says so, rather than differing from a read one only by the
 * absence of a label.
 */
export type CouchTopMethod = 'body' | 'image' | 'default';

export interface CouchTopFinding {
    /** Continuous voxel Y in the grid it was found against. */
    readonly voxelY: number;
    readonly method: CouchTopMethod;
}

export interface CouchTopInput {
    /** The body structure's signed-distance field, when the set has one. */
    readonly body: StructureSdf | null;
    /** Hounsfield units in Image3D order, or null when the volume cannot be
     *  read (still arriving, or not HU-quantified). */
    readonly voxels: Int16Array | null;
    readonly grid: Grid;
}

/**
 * Where the couch top appears to be, or null when neither reading finds
 * one. An estimate to drag from, never a measurement — both readings are
 * inferences about a surface neither of them observes directly.
 *
 * BODY first, when there is one. It is the more reliable of the two
 * because a body contour is a reviewed object: someone has already looked
 * at it, so its lower edge fails loudly rather than quietly. The image
 * reading needs the couch to be in the field and the insert to be flat,
 * and has only its own thresholds to tell couch from blanket.
 *
 * The trade is that BODY measures the patient, not the couch, so it reads
 * as deep as the pad compresses under them; the image reading measures the
 * couch itself. Where the two disagree it is usually by that much.
 */
export function findCouchTop({
    body,
    voxels,
    grid,
}: CouchTopInput): (CouchTopFinding & { method: 'body' | 'image' }) | null {
    if (body) {
        const voxelY = couchTopFromBody(body, grid);
        if (voxelY !== null) {
            return { voxelY, method: 'body' };
        }
    }
    if (voxels) {
        const voxelY = couchTopFromImage(voxels, grid);
        if (voxelY !== null) {
            return { voxelY, method: 'image' };
        }
    }
    return null;
}
